/**
 * Welfare validity period ("สวัสดิการนี้ใช้กับปีไหน").
 * Stored on the catalog item as { type: always | year | range, year_from, year_to, start_date, end_date };
 * the API (admin-helpers.normalizePeriod) resolves a พ.ศ. year to 1 Jan – 31 Dec and the engine
 * (engine.isWelfareInPeriod) only compares the inclusive ISO dates against today's Thai date.
 */
export const BE_OFFSET = 543;
export const TH_MONTHS = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
export const TH_MONTHS_SHORT = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

/** today in Thailand as YYYY-MM-DD (same rule as the engine) */
export const todayISO = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
export const currentYearBE = () => Number(todayISO().slice(0, 4)) + BE_OFFSET;

export const blankPeriod = () => ({ type: "always", year_from: null, year_to: null, start_date: null, end_date: null });
export const yearPeriod = (year = currentYearBE()) => ({ type: "year", year_from: year, year_to: year, start_date: null, end_date: null });

/** "2026-09-30" -> "30 ก.ย. 2569" */
export function formatThaiDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return "";
  return `${Number(m[3])} ${TH_MONTHS_SHORT[Number(m[2]) - 1]} ${Number(m[1]) + BE_OFFSET}`;
}

/** inclusive ISO bounds; works on a form value that has not been normalized by the API yet */
export function periodBounds(p) {
  if (!p || p.type === "always" || !p.type) return { start: null, end: null };
  if (p.type === "year") {
    const from = Number(p.year_from);
    const to = p.year_to === null || p.year_to === undefined || p.year_to === "" ? null : Number(p.year_to);
    return { start: from ? `${from - BE_OFFSET}-01-01` : null, end: to ? `${to - BE_OFFSET}-12-31` : null };
  }
  return { start: p.start_date || null, end: p.end_date || null };
}

/** always | current | upcoming | expired — relative to `day` */
export function periodStatus(p, day = todayISO()) {
  if (!p || p.type === "always" || !p.type) return "always";
  const { start, end } = periodBounds(p);
  if (start && day < start) return "upcoming";
  if (end && day > end) return "expired";
  return "current";
}
export const isUsableToday = (p, day) => ["always", "current"].includes(periodStatus(p, day));

export function periodLabel(p) {
  if (!p || p.type === "always" || !p.type) return "ทุกปี";
  if (p.type === "year") {
    if (!p.year_from) return "ยังไม่ระบุปี";
    if (!p.year_to) return `ตั้งแต่ปี ${p.year_from}`;
    return Number(p.year_to) === Number(p.year_from) ? `ปี ${p.year_from}` : `ปี ${p.year_from}–${p.year_to}`;
  }
  const { start, end } = periodBounds(p);
  if (start && end) return `${formatThaiDate(start)} – ${formatThaiDate(end)}`;
  if (start) return `ตั้งแต่ ${formatThaiDate(start)}`;
  if (end) return `ถึง ${formatThaiDate(end)}`;
  return "ยังไม่ระบุวันที่";
}

export const PERIOD_STATUS_LABEL = { always: "ทุกปี", current: "ใช้ได้วันนี้", upcoming: "ยังไม่เริ่ม", expired: "หมดอายุแล้ว" };
export const PERIOD_STATUS_STYLE = {
  always: { color: "#728096", background: "#f1f5f8", border: "1px solid #e2e8f0" },
  current: { color: "#1f6b45", background: "#e6f5ec", border: "1px solid #b7e1c8" },
  upcoming: { color: "#B36B00", background: "#fdf3df", border: "1px solid #f0dcae" },
  expired: { color: "#CE3131", background: "#fcebeb", border: "1px solid #f2c9c9" },
};

/** does the period overlap calendar year `yearBE` (พ.ศ.)? "every year" always does */
export function periodCoversYear(p, yearBE) {
  const { start, end } = periodBounds(p);
  const y = Number(yearBE) - BE_OFFSET;
  return (!start || start <= `${y}-12-31`) && (!end || end >= `${y}-01-01`);
}

/** filter choices for a list of catalog items: status first, then every year that appears in a period */
export function periodFilterOptions(items = []) {
  const now = currentYearBE();
  const years = new Set([now, now + 1]);
  for (const it of items) {
    const { start, end } = periodBounds(it && it.period);
    const a = start ? Number(start.slice(0, 4)) + BE_OFFSET : null;
    const b = end ? Number(end.slice(0, 4)) + BE_OFFSET : null;
    if (a && b) for (let y = a; y <= Math.min(b, a + 30); y++) years.add(y);
    else if (a || b) years.add(a || b);
  }
  return [
    { value: "current", label: "ใช้ได้วันนี้" },
    { value: "", label: "ทุกช่วงเวลา" },
    ...[...years].sort((x, y) => y - x).map((y) => ({ value: `y:${y}`, label: `ใช้ได้ในปี ${y}` })),
    { value: "upcoming", label: "ยังไม่เริ่ม" },
    { value: "expired", label: "หมดอายุแล้ว" },
  ];
}
export function matchPeriodFilter(p, value, day = todayISO()) {
  if (!value) return true;
  if (value === "current") return isUsableToday(p, day);
  if (value.startsWith("y:")) return periodCoversYear(p, Number(value.slice(2)));
  return periodStatus(p, day) === value;
}

/** Thai fiscal year Y = 1 Oct (Y-1) – 30 Sep Y, offered as a shortcut in the date-range mode */
export const fiscalYearRange = (yearBE) => ({ start_date: `${yearBE - BE_OFFSET - 1}-10-01`, end_date: `${yearBE - BE_OFFSET}-09-30` });

/** client-side check mirroring admin-helpers.validatePeriod; returns a Thai message or "" */
export function periodError(p) {
  if (!p || p.type === "always") return "";
  if (p.type === "year") {
    if (!p.year_from) return "ต้องระบุปี พ.ศ. ที่เริ่มใช้";
    if (p.year_to && Number(p.year_to) < Number(p.year_from)) return "ปีที่สิ้นสุดต้องไม่น้อยกว่าปีที่เริ่ม";
    return "";
  }
  if (!p.start_date && !p.end_date) return "ต้องระบุวันที่เริ่มหรือวันที่สิ้นสุดอย่างน้อย 1 ช่อง";
  if (p.start_date && p.end_date && p.end_date < p.start_date) return "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม";
  return "";
}

/** body value for the API: null = every year */
export function periodPayload(p) {
  if (!p || p.type === "always") return null;
  if (p.type === "year") return { type: "year", year_from: Number(p.year_from), year_to: p.year_to ? Number(p.year_to) : null };
  return { type: "range", start_date: p.start_date || null, end_date: p.end_date || null };
}
